/**
 * Replay identity: normalized serialization + deterministic hashing.
 *
 * MVP criteria 1 and 11: re-running the same input bundle and seed must produce
 * byte-equivalent normalized outputs, and every result carries the exact input
 * identity (hash) so a run can be replayed and audited without secrets.
 */

/** JSON.stringify with recursively sorted object keys (stable across runs). */
export function stableStringify(value: unknown): string {
  return JSON.stringify(sortValue(value));
}

function sortValue(value: unknown): unknown {
  if (Array.isArray(value)) return value.map(sortValue);
  if (value !== null && typeof value === 'object') {
    const record = value as Record<string, unknown>;
    const sorted: Record<string, unknown> = {};
    for (const key of Object.keys(record).sort()) {
      sorted[key] = sortValue(record[key]);
    }
    return sorted;
  }
  return value;
}

/** FNV-1a 32-bit over the normalized JSON — same family as the web A/B routing hash. */
export function fnv1aHash(input: string): string {
  let hash = 0x811c9dc5;
  for (let i = 0; i < input.length; i += 1) {
    hash ^= input.charCodeAt(i);
    hash = Math.imul(hash, 0x01000193);
  }
  return (hash >>> 0).toString(16).padStart(8, '0');
}

export function hashInputBundle(bundle: unknown): string {
  return fnv1aHash(stableStringify(bundle));
}

/** Round to a fixed number of decimals so replay comparison is byte-exact. */
export function normalizeNumber(value: number, decimals = 6): number {
  const factor = Math.pow(10, decimals);
  return Math.round(value * factor) / factor;
}
